import {
  useMemo, createContext, useContext, ReactNode,
} from 'react';
import styled, { FlattenInterpolation, ThemeProps } from 'styled-components';
import _ from 'lodash';
import { getFieldIssueMessages } from '../../../util/getFieldIssueMessages';
import { FlexGrow } from '../../Flex';
import { getErrors, ValidationFunction } from '../../../validator';
import { ThemeType } from '../../../styling';
import { Errors } from '../../../..';

export type ModelType = { [key: string]: any };
export type UpdateType = (model: ModelType, disableUpdateCountForTextInputs?: boolean) => void;
export type ValidationType = { [key: string]: ValidationFunction[] };
export type VisibilityType = { [key: string]: (model: ModelType) => boolean };

type ContextType = {
  model: ModelType,
  update: UpdateType,
  validation?: ValidationType,
  visibility?: VisibilityType,
  errors?: Errors,
  warnings?: Errors
}

export const ModelFormContext = createContext<ContextType>({
  model: {},
  update: () => { },
});

export const FormInput = styled(FlexGrow) <{ visible?: boolean, css?: FlattenInterpolation<ThemeProps<ThemeType>> | string }>`
  ${({ visible = true }) => `
    ${visible ? '' : 'display: none;'}
  `};
  
  ${({ css }) => css};
`;

export const useModelFormUpdate = () => useContext(ModelFormContext)?.update;

export const useModelForm = (
  field: string,
  valueOverride?: any,
  errorOverride?: string,
  updateOverride?: UpdateType,
) => {
  const {
    model, update, validation, visibility, errors, warnings,
  } = useContext(ModelFormContext);

  const validators = _.get(validation, field) ?? [];
  const value = valueOverride ?? _.get(model, field) ?? '';
  const fieldErrors = getFieldIssueMessages(field, model, errors ?? model?.errors ?? {});
  const fieldWarnings = getFieldIssueMessages(field, model, warnings ?? model?.warnings ?? {});
  const validatorErrors = getErrors(value, validators, model).length ? getErrors(value, validators, model) : null;

  return {
    value,
    update: updateOverride ?? update,
    errors: validatorErrors ?? (errorOverride ? [errorOverride] : fieldErrors),
    warnings: fieldWarnings,
    visible: visibility && _.get(visibility, field) ? _.get(visibility, field)(model) : true,
  };
};

export function ModelForm({
  children,
  model,
  update,
  validation,
  visibility,
  errors,
  warnings,
}: {
  children: ReactNode,
  model: ModelType,
  update: UpdateType,
  validation?: ValidationType,
  visibility?: VisibilityType,
  errors?: Errors,
  warnings?: Errors,
}) {
  const value = useMemo<ContextType>(() => ({
    model, update, validation, visibility, errors, warnings,
  }), [model, update, validation, visibility, errors, warnings]);

  return (
    <ModelFormContext.Provider value={value}>
      {children}
    </ModelFormContext.Provider>
  );
}
